import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useTitleBarSelect from '../../../hooks/useTitleBarSelect';
import { Select } from '../../../types/redux/pages/PagesStore';
import {
  addAdditionalTitleBarComponents,
  removeTitleBarComponent,
} from '../../../redux/pages/actions';
import {
  APPEND_NEW_SELECT_TO_TITLEBAR,
  REMOVE_SELECT_FROM_TITLEBAR,
} from '../../../types/redux/pages/pageTypes';
import YieldCurve from './subComponents/YieldCurve';
import YieldTable from './subComponents/YieldTable';
import { GeneralOverviewPageRenderedComponentProps } from '../../layout/general/SimpleGeneralPageWrapper';
import {
  useYieldCurveQuery,
  type YieldCurvePayload,
} from '../../../api/rs-hg/markets';
import { QueryBoundary } from '../../utilityComponents/QueryBoundary';

export interface YieldDataType {
  matur: number;
  par: number;
  spot: number;
  forward: number;
}

export interface YieldDataProps {
  yieldData: YieldDataType[];
}

const mapYieldCurvePayload = (data: YieldCurvePayload): YieldDataType[] =>
  data.yield_curve_data.slice(1).map((row) => ({
    matur: row[0],
    par: row[1],
    spot: row[2],
    forward: row[3],
  }));

const YieldPage: React.FC<GeneralOverviewPageRenderedComponentProps> = (
  props,
) => {
  const dispatch = useDispatch();
  const curve_selected = useTitleBarSelect('Curve') || 'swap';
  const currency_selected = useTitleBarSelect('Currency') || 'EUR';

  useEffect(() => {
    const selectsToAdd_curve: Select[] = [
      {
        titleBarTitle: 'Curve:',
        titleBarKey: 'Curve',
        values: [
          {
            label: 'Swap',
            value: 'swap',
          },
        ],
        displayOnOverviewPage: true,
        aValueIsAlwaysSelected: true,
        currentValue: 'swap',
      },
    ];
    const selectsToAdd_currency: Select[] = [
      {
        titleBarTitle: 'Currency:',
        titleBarKey: 'Currency',
        values: [
          {
            label: 'EUR',
            value: 'EUR',
          },
          {
            label: 'GBP',
            value: 'GBP',
          },
          {
            label: 'USD',
            value: 'USD',
          },
          {
            label: 'CHF',
            value: 'CHF',
          },
          {
            label: 'JPY',
            value: 'JPY',
          },
          {
            label: 'CNY',
            value: 'CNY',
          },
        ],
        displayOnOverviewPage: true,
        aValueIsAlwaysSelected: true,
        currentValue: 'EUR',
      },
    ];
    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd_curve,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );
    dispatch(
      addAdditionalTitleBarComponents(
        selectsToAdd_currency,
        APPEND_NEW_SELECT_TO_TITLEBAR,
      ),
    );

    return () => {
      dispatch(removeTitleBarComponent('Curve', REMOVE_SELECT_FROM_TITLEBAR));
      dispatch(
        removeTitleBarComponent('Currency', REMOVE_SELECT_FROM_TITLEBAR),
      );
    };
  }, [dispatch]);

  const yieldCurveQuery = useYieldCurveQuery(
    currency_selected,
    curve_selected,
    props.positionDate,
  );

  return (
    <QueryBoundary<YieldCurvePayload>
      isLoading={yieldCurveQuery.isLoading}
      isFetching={yieldCurveQuery.isFetching}
      isError={yieldCurveQuery.isError}
      error={yieldCurveQuery.error}
      data={yieldCurveQuery.data?.data}
      loadingMessages={['Loading Yield Data...', '...']}
      errorMessage="No Data Found"
      noDataMessage="No yield curve data available."
    >
      {(data) => {
        const yieldData = mapYieldCurvePayload(data);
        return (
          <div className="grid w-full grid-cols-[1fr_3fr] gap-5 p-5 lg:max-[1440px]:grid-cols-[1fr_2fr] lg:max-[1440px]:gap-2.5 lg:max-[1440px]:p-2.5">
            <YieldTable yieldData={yieldData} />
            <YieldCurve yieldData={yieldData} />
          </div>
        );
      }}
    </QueryBoundary>
  );
};

export default YieldPage;
